import React, { useEffect, useRef, useState, useCallback } from 'react'
import {
  TrendingUp, TrendingDown, CreditCard, Briefcase,
  Percent, Wallet, Calendar, CalendarCheck, BarChart3,
  AlertCircle
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Chart, registerables } from 'chart.js'
import { apiCall } from '@/api/apiCall'

Chart.register(...registerables)

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const fmt = (val) => {
  if (val === null || val === undefined || val === '0') return '0'
  return val
}

// ─────────────────────────────────────────────
// Shared Legend
// ─────────────────────────────────────────────
const Legend = ({ items }) => (
  <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginBottom: 14 }}>
    {items.map(({ color, label }) => (
      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 500, color: '#3a5a72' }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
        {label}
      </div>
    ))}
  </div>
)

// ─────────────────────────────────────────────
// Stat Card
// ─────────────────────────────────────────────
const StatCard = ({ label, value, icon: Icon, badge, progress }) => (
  <div style={{
    background: '#DDEBF7', borderRadius: 14, padding: '16px 18px 18px',
    display: 'flex', flexDirection: 'column', gap: 8, minHeight: 100,
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <span style={{
        fontSize: 10, fontWeight: 600, color: '#5a7a99',
        letterSpacing: '0.04em', textTransform: 'uppercase', lineHeight: 1.4, maxWidth: '80%'
      }}>
        {label}
      </span>
      {Icon && (
        <div style={{
          width: 28, height: 28, background: 'white', borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon size={15} color="#2b7fc7" strokeWidth={2.2} />
        </div>
      )}
    </div>
    <div style={{ fontSize: 24, fontWeight: 700, color: '#1a2e42', lineHeight: 1 }}>{value ?? '—'}</div>
    {badge && (
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        fontSize: 11, fontWeight: 600, padding: '3px 7px', borderRadius: 20, width: 'fit-content',
        background: badge.type === 'down' ? '#fde8e8' : '#e6f9f0',
        color: badge.type === 'down' ? '#e03131' : '#2f9e44',
      }}>
        {badge.type === 'down'
          ? <TrendingDown size={10} strokeWidth={2.5} />
          : <TrendingUp size={10} strokeWidth={2.5} />}
        {badge.text}
      </div>
    )}
    {progress !== undefined && (
      <div style={{ height: 5, background: '#b8d4ec', borderRadius: 10, overflow: 'hidden', marginTop: 4 }}>
        <div style={{ height: '100%', width: `${progress}%`, background: '#2b7fc7', borderRadius: 10 }} />
      </div>
    )}
  </div>
)

// ─────────────────────────────────────────────
// Cash Inflows Bar Chart
// ─────────────────────────────────────────────
const CashInflowsChart = ({ inflows }) => {
  const ref = useRef(null)
  const chartRef = useRef(null)

  useEffect(() => {
    if (!inflows?.length) return
    const filtered = inflows.filter(i => !i.is_total)
    const labels = filtered.map(i => i.label)
    const actuals = filtered.map(i => parseFloat(i.actual.replace(/,/g, '')))
    const forecasts = filtered.map(i => parseFloat(i.forecast.replace(/,/g, '')))

    if (chartRef.current) chartRef.current.destroy()
    chartRef.current = new Chart(ref.current.getContext('2d'), {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Actual',
            data: actuals,
            backgroundColor: '#3B82F6',
            borderRadius: 4,
            borderSkipped: false,
            barPercentage: 0.7,
            categoryPercentage: 0.7,
          },
          {
            label: 'Forecast',
            data: forecasts,
            backgroundColor: '#FF7043',
            borderRadius: 4,
            borderSkipped: false,
            barPercentage: 0.7,
            categoryPercentage: 0.7,
          },
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { 
  grid: { display: false }, 
  ticks: { 
    font: { size: 8 },     // ← thoda chota
    color: '#6b8fa8', 
    maxRotation: 0, 
    minRotation: 0,
    autoSkip: false,       // ← yeh hata do ya false karo
  } 
},
          y: {
  grid: { 
    color: (context) => context.tick.value === 0 
      ? 'rgba(0,0,0,0.2)'   // sirf zero line dikhegi
      : 'transparent',       // baaki sab hatao
  },
  ticks: { font: { size: 10 }, color: '#6b8fa8', callback: v => v.toLocaleString() }
}
        }
      }
    })
    return () => chartRef.current?.destroy()
  }, [inflows])

  return (
    <div style={{ background: '#DDEBF7', borderRadius: 14, padding: '20px 22px', flex: '0 0 58%' }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#1a2e42', marginBottom: 2 }}>Cash Inflows</div>
      <div style={{ fontSize: 11, color: '#6b8fa8', marginBottom: 14 }}>Actual vs Forecast by category</div>
      <Legend items={[
        { color: '#3B82F6', label: 'Actual' },
        { color: '#FF7043', label: 'Forecast' },
      ]} />
      <div style={{ height: 260 }}>
        <canvas ref={ref} />
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// Cash Outflows Line Chart
// ─────────────────────────────────────────────
const CashOutflowsChart = ({ outflows }) => {
  const ref = useRef(null)
  const chartRef = useRef(null)

  useEffect(() => {
    if (!outflows?.length) return
    const parents = outflows.filter(i => !i.is_sub && !i.is_total)
    const labels = parents.map(i => i.label)
    const actuals = parents.map(i => parseFloat(i.actual.replace(/,/g, '')))
    const forecasts = parents.map(i => parseFloat(i.forecast.replace(/,/g, '')))

    if (chartRef.current) chartRef.current.destroy()
    chartRef.current = new Chart(ref.current.getContext('2d'), {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Actual',
            data: actuals,
            borderColor: '#3B82F6',
            backgroundColor: 'rgba(59,130,246,0.1)',
            pointBackgroundColor: '#3B82F6',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7,
            borderWidth: 2.5,
            tension: 0,
            fill: true,
          },
          {
            label: 'Forecast',
            data: forecasts,
            borderColor: '#FF7043',
            backgroundColor: 'rgba(255,112,67,0.08)',
            pointBackgroundColor: '#FF7043',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7,
            borderWidth: 2.5,
            tension: 0,
            fill: true,
          },
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
         x: { 
  grid: { display: false }, 
  ticks: { 
    font: { size: 8 },     // ← thoda chota
    color: '#6b8fa8', 
    maxRotation: 0, 
    minRotation: 0,
    autoSkip: false,       // ← yeh hata do ya false karo
  } 
},
          y: {
  grid: { 
    color: (context) => context.tick.value === 0 
      ? 'rgba(0,0,0,0.2)'   // sirf zero line dikhegi
      : 'transparent',       // baaki sab hatao
  },
  ticks: { font: { size: 10 }, color: '#6b8fa8', callback: v => v.toLocaleString() }
}
        }
      }
    })
    return () => chartRef.current?.destroy()
  }, [outflows])

  return (
    <div style={{ background: '#DDEBF7', borderRadius: 14, padding: '20px 22px', flex: 1 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#1a2e42', marginBottom: 2 }}>Cash Outflows</div>
      <div style={{ fontSize: 11, color: '#6b8fa8', marginBottom: 14 }}>Actual vs Forecast by category</div>
      <Legend items={[
        { color: '#3B82F6', label: 'Actual' },
        { color: '#FF7043', label: 'Forecast' },
      ]} />
      <div style={{ height: 260 }}>
        <canvas ref={ref} />
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// Outflows Detail Table (with Expand/Collapse)
// ─────────────────────────────────────────────
const OutflowsTable = ({ outflows }) => {
  const buildSections = (rows) => {
    const sections = []
    rows.forEach((row) => {
      if (row.is_total) return
      if (!row.is_sub) {
        sections.push({ parent: row, children: [] })
      } else {
        if (sections.length > 0) {
          sections[sections.length - 1].children.push(row)
        }
      }
    })
    return sections
  }

  const sections = buildSections(outflows || [])
  const totalRow = outflows?.find((r) => r.is_total)

  const initExpanded = sections.reduce((acc, s) => {
    acc[s.parent.label] = false
    return acc
  }, {})
  const [expandedParents, setExpandedParents] = useState(initExpanded)

  const toggleParent = (label) => {
    setExpandedParents((prev) => ({ ...prev, [label]: !prev[label] }))
  }

  const UpArrow   = () => <span style={{ color: "#70AD47", fontSize: 10 }}>▲</span>
  const DownArrow = () => <span style={{ color: "#C90A0A", fontSize: 10 }}>▼</span>

  const isNegativeDiff = (val) => val?.startsWith('(') || (val && parseFloat(val) < 0)

  return (
    <div style={{ background: "var(--card)", borderRadius: 14, padding: "20px 22px", border: "1px solid var(--border)" }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--foreground)", marginBottom: 2 }}>
        Outflows Detail
      </div>
      <div style={{ fontSize: 11, color: "var(--muted-foreground)", marginBottom: 14 }}>
        Actual vs Forecast vs Difference
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr>
              {["Category", "Actual", "Forecast", "Difference"].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: h === "Category" ? "left" : "right",
                    padding: "10px 14px",
                    fontWeight: 700,
                    color: "var(--foreground)",
                    fontSize: 11,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    borderBottom: "2px solid var(--border)",
                    whiteSpace: "nowrap",
                    background: "var(--card)",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sections.map((section, idx) => {
              const { parent, children } = section
              const isExpanded   = expandedParents[parent.label]
              const hasChildren  = children.length > 0
              const parentDiffNeg = isNegativeDiff(parent.diff)

              return (
                <React.Fragment key={idx}>
                  <tr
                    style={{ background: "#edf5fb", cursor: hasChildren ? "pointer" : "default" }}
                    onClick={() => hasChildren && toggleParent(parent.label)}
                  >
                    <td style={{ padding: "10px 14px", fontWeight: 700, color: "var(--foreground)", borderBottom: "1px solid var(--table-border-light)", whiteSpace: "nowrap" }}>
                      {hasChildren && (
                        <span style={{ marginRight: 8, fontSize: 9, color: "#4472C4", display: "inline-block", width: 12 }}>
                          {isExpanded ? "▼" : "▶"}
                        </span>
                      )}
                      {parent.label}
                    </td>
                    <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: "var(--foreground)", borderBottom: "1px solid var(--table-border-light)", fontFamily: "monospace" }}>
                      {fmt(parent.actual)}
                    </td>
                    <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: "var(--foreground)", borderBottom: "1px solid var(--table-border-light)", fontFamily: "monospace" }}>
                      {fmt(parent.forecast)}
                    </td>
                    <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: parentDiffNeg ? "#C90A0A" : "#70AD47", borderBottom: "1px solid var(--table-border-light)", fontFamily: "monospace" }}>
                      {fmt(parent.diff)} {parentDiffNeg ? <DownArrow /> : <UpArrow />}
                    </td>
                  </tr>

                  {isExpanded && children.map((child, cIdx) => {
                    const childDiffNeg = isNegativeDiff(child.diff)
                    return (
                      <tr key={cIdx} style={{ background: "#f9fbfd" }}>
                        <td style={{ padding: "8px 14px", paddingLeft: 32, color: "var(--muted-foreground)", borderBottom: "1px solid var(--table-border-light)", whiteSpace: "nowrap" }}>
                          {child.label}
                        </td>
                        <td style={{ padding: "8px 14px", textAlign: "right", color: "var(--foreground)", borderBottom: "1px solid var(--table-border-light)", fontFamily: "monospace" }}>
                          {fmt(child.actual)}
                        </td>
                        <td style={{ padding: "8px 14px", textAlign: "right", color: "var(--foreground)", borderBottom: "1px solid var(--table-border-light)", fontFamily: "monospace" }}>
                          {fmt(child.forecast)}
                        </td>
                        <td style={{ padding: "8px 14px", textAlign: "right", color: childDiffNeg ? "#C90A0A" : "#70AD47", borderBottom: "1px solid var(--table-border-light)", fontFamily: "monospace" }}>
                          {fmt(child.diff)} {childDiffNeg ? <DownArrow /> : <UpArrow />}
                        </td>
                      </tr>
                    )
                  })}
                </React.Fragment>
              )
            })}

            {totalRow && (
              <tr style={{ background: "#edf5fb" }}>
                <td style={{ padding: "10px 14px", fontWeight: 700, color: "var(--foreground)", borderTop: "1.5px solid #aaa", borderBottom: "1.5px solid #aaa" }}>
                  {totalRow.label}
                </td>
                <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: "var(--foreground)", borderTop: "1.5px solid #aaa", borderBottom: "1.5px solid #aaa", fontFamily: "monospace" }}>
                  {fmt(totalRow.actual)}
                </td>
                <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: "var(--foreground)", borderTop: "1.5px solid #aaa", borderBottom: "1.5px solid #aaa", fontFamily: "monospace" }}>
                  {fmt(totalRow.forecast)}
                </td>
                <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: totalRow.diff?.startsWith("(") ? "#C90A0A" : "#70AD47", borderTop: "1.5px solid #aaa", borderBottom: "1.5px solid #aaa", fontFamily: "monospace" }}>
                  {fmt(totalRow.diff)} {totalRow.diff?.startsWith("(") ? <span style={{ color: "#C90A0A", fontSize: 10 }}>▼</span> : <span style={{ color: "#70AD47", fontSize: 10 }}>▲</span>}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// Inflows Polar Area Chart
// ─────────────────────────────────────────────
const InflowsChart = ({ inflows }) => {
  const ref      = useRef(null)
  const chartRef = useRef(null)

  const allInflows  = inflows?.filter(i => !i.is_total) || []
  const labels      = allInflows.map(i => i.label)
  const values      = allInflows.map(i => parseFloat(i.actual.replace(/,/g, '')))
  const totalActual = values.reduce((sum, v) => sum + v, 0)

  const colors = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
    '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'
  ]

  useEffect(() => {
    if (!ref.current || !values.length) return
    if (chartRef.current) chartRef.current.destroy()

    chartRef.current = new Chart(ref.current.getContext('2d'), {
      type: 'polarArea',
      data: {
        labels,
        datasets: [{
          data: values,
          backgroundColor: colors.slice(0, values.length),
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'right', labels: { font: { size: 10 }, color: '#3a5a72' } },
          tooltip: {
            callbacks: {
              label: (context) => {
                const value   = context.raw
                const percent = ((value / totalActual) * 100).toFixed(1)
                return `${context.label}: ${value.toLocaleString()} (${percent}%)`
              }
            }
          }
        },
        scales: {
          r: {
            ticks: { display: false },
            grid:  { color: 'rgba(0,0,0,0.05)' }
          }
        }
      }
    })
    return () => { if (chartRef.current) chartRef.current.destroy() }
  }, [inflows])

  if (!values.length) {
    return (
      <div style={{ background: '#DDEBF7', borderRadius: 14, padding: 20 }}>
        No data available
      </div>
    )
  }

  return (
    <div style={{ background: '#DDEBF7', borderRadius: 14, padding: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#1a2e42' }}>Inflows Breakdown</div>
      <div style={{ fontSize: 11, color: '#6b8fa8', marginBottom: 10 }}>Category-wise distribution of cash inflows</div>
      <div style={{ height: 245 }}>
        <canvas ref={ref} />
      </div>
      <div style={{ marginTop: 10, textAlign: 'right' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#1a2e42' }}>{totalActual.toLocaleString()}</div>
        <div style={{ fontSize: 10, color: '#6b8fa8' }}>Total Inflows</div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// Loading Skeleton
// ─────────────────────────────────────────────
const Skeleton = ({ h = 100, br = 14 }) => (
  <div style={{
    height: h,
    background: 'linear-gradient(90deg, #DDEBF7 25%, #c8dff0 50%, #DDEBF7 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
    borderRadius: br,
  }} />
)

// ══════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════
const ForecastPage = ({
  selectedContract,
  selectedContractLabel,
  selectedDisplay,
  selectedCurrency,
  selectedYear,
  selectedMonth,
}) => {
  const [data,       setData]       = useState(null)
  const [loading,    setLoading]    = useState(false)
  const [error,      setError]      = useState(null)
  const [hasApplied, setHasApplied] = useState(false)

  const fetchData = useCallback(async () => {
    if (!selectedContract || !selectedDisplay || !selectedCurrency || !selectedYear || !selectedMonth) return
    setLoading(true)
    setError(null)
    try {
      const params = {
        contract: selectedContract,
        year:     selectedYear,
        month:    selectedMonth,
        display:  selectedDisplay,
        currency: selectedCurrency,
      }
      const res = await apiCall('get', '/get-cash-flow', params, null, false, false)
      if (res?.success) {
        setData(res.data)
      } else {
        setError('Failed to load cash flow data')
      }
    } catch (e) {
      setError('Failed to load cash flow data')
    } finally {
      setLoading(false)
    }
  }, [selectedContract, selectedDisplay, selectedCurrency, selectedYear, selectedMonth])

  useEffect(() => {
    if (selectedContract && selectedDisplay && selectedCurrency && selectedYear && selectedMonth) {
      setHasApplied(true)
      fetchData()
    } else {
      setHasApplied(false)
      setData(null)
      setError(null)
    }
  }, [fetchData])

  const cards    = data?.cards || []
  const getCard  = (label) => cards.find(c => c.label === label)?.value ?? '—'
  const pctCollected = getCard('% Collected')
  const pctNum       = parseFloat(pctCollected) || 0

  const cardConfigs = [
    { label: 'Opening Cash Balance',       value: getCard('Opening Cash Balance'),       icon: Wallet      },
    { label: 'Closing Balance',            value: getCard('Closing Balance'),            icon: CreditCard  },
    { label: 'Cash Inflow / (Outflow)',    value: getCard('Cash Inflow / (Outflow)'),    icon: TrendingUp  },
    { label: 'Total Cash Inflows',         value: getCard('Total Cash Inflows'),         icon: Briefcase   },
  ]

  const bottomCards = [
    { label: '% Collected',       value: pctCollected,                    progress: pctNum, icon: Percent      },
    { label: 'Monthly Avg (YTD)', value: getCard('Monthly Avg (YTD)'),                      icon: BarChart3    },
    { label: 'Days In Month',     value: getCard('Days In Month'),                          icon: Calendar     },
    { label: 'Days Passed',       value: getCard('Days Passed'),                            icon: CalendarCheck },
  ]

  // ── Guard: filters not selected ──
  if (!hasApplied) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <CardTitle className="text-destructive flex items-center gap-2 justify-center">
              <AlertCircle className="w-5 h-5" />
              <span>Select filters to view Cash Flow.</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Please select Contract, Display and Currency to view the Cash Flow
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // ── Loading ──
  if (loading) {
    return (
      <div style={{ padding: 24, fontFamily: "'Segoe UI', sans-serif" }}>
        <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 14 }}>
          {[...Array(4)].map((_, i) => <Skeleton key={i} h={110} />)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 14 }}>
          {[...Array(4)].map((_, i) => <Skeleton key={i} h={110} />)}
        </div>
        <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
          <Skeleton h={320} br={14} />
          <Skeleton h={320} br={14} />
        </div>
        <Skeleton h={400} br={14} />
      </div>
    )
  }

  // ── Error ──
  if (error) {
    return (
      <div style={{ padding: 24, fontFamily: "'Segoe UI', sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <div style={{ background: '#fde8e8', color: '#e03131', padding: '16px 24px', borderRadius: 12, fontWeight: 600, fontSize: 14 }}>
          {error}
        </div>
      </div>
    )
  }

  // ── No data (shouldn't normally hit this since hasApplied guards above) ──
  if (!data) return null

  return (
    <div style={{ padding: 24, minHeight: '100vh', fontFamily: "'Segoe UI', sans-serif" }}>
      <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>

      {/* Top 4 Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 14 }}>
        {cardConfigs.map((card, i) => <StatCard key={i} {...card} />)}
      </div>

      {/* Bottom 4 Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 14 }}>
        {bottomCards.map((card, i) => <StatCard key={i} {...card} />)}
      </div>

      {/* Charts Row */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
        <div style={{ width: '40%' }}>
          <CashInflowsChart inflows={data.inflows} />
        </div>
        <div style={{ width: '40%' }}>
          <CashOutflowsChart outflows={data.outflows} />
        </div>
        <div style={{ width: '20%' }}>
          <InflowsChart inflows={data.inflows} />
        </div>
      </div>

      {/* Outflows Table */}
      <div style={{ display: 'flex', gap: 14, flexDirection: 'column' }}>
        <OutflowsTable outflows={data.outflows} />
      </div>
    </div>
  )
}

export default ForecastPage